import { useMutation, useQuery } from '@tanstack/react-query'

import { useApi } from '@tabeeb/modules/shared/utils/API/hooks'
import { queryClient } from '@tabeeb/services/queryClient'

import { AIQueryKeys } from '../queries'

const useVQAModelForms = ({ formId, enabled }: { formId: number; enabled: boolean }) => {
  const { aiServiceApi: api } = useApi()

  const queryKey = [AIQueryKeys.VQAModelForms, formId]

  const query = useQuery({
    queryKey,
    queryFn: () => api.vqaModels.getByFormIdAsync(formId),
    cacheTime: Infinity,
    refetchInterval: false,
    enabled,
  })

  const attachVQAModelMutation = useMutation({
    mutationFn: async ({ FormId, VQAModelId }: { FormId: number; VQAModelId: number }) =>
      api.vqaModels.addToFormAsync(FormId, VQAModelId),
    onSuccess: (response) => {
      queryClient.setQueryData<typeof query['data']>(queryKey, (prev) => {
        return [response.VQAModel]
      })
    },
  })

  const detachVQAModelMutation = useMutation({
    mutationFn: async ({ FormId, VQAModelId }: { FormId: number; VQAModelId: number }) =>
      api.vqaModels.deleteFromFormAsync(FormId, VQAModelId),
    onSuccess: (_, request) => {
      queryClient.setQueryData<typeof query['data']>(queryKey, (prev) => {
        if (!prev) {
          return prev
        }

        return prev.filter((vqaModel) => vqaModel.Id !== request.VQAModelId)
      })
    },
  })

  return {
    loading: query.isLoading || query.isFetching,
    loaded: query.isSuccess,
    vqaModels: query.data || [],
    updating: attachVQAModelMutation.isLoading || detachVQAModelMutation.isLoading,
    onAttachVQAModel: attachVQAModelMutation.mutate,
    onDetachVQAModel: detachVQAModelMutation.mutate,
  }
}

export default useVQAModelForms
